/** @format */

import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Box, CircularProgress, Typography } from "@mui/material";

import {
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionSubtitle,
    CollectSectionTitle,
} from "../styles";
import { colors } from "../../../utility/types/constant";
import { IDashboardOverview } from "../../../utility/types/collectTypes";

interface Props {
    overview: IDashboardOverview | null;
    isLoading: boolean;
}

const TIER_COLORS: Record<string, string> = {
    critical: "#dc2626",
    high: "#ea580c",
    medium: "#eab308",
    low: "#16a34a",
};

const TIER_LABELS: Record<string, string> = {
    critical: "Critical (70+)",
    high: "High (50–69)",
    medium: "Medium (25–49)",
    low: "Low (<25)",
};

function fmtCurrency(amount: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `$${amount.toFixed(0)}`;
    }
}

const UrgencyChart: React.FC<Props> = ({ overview, isLoading }) => {
    const tierOrder: Array<"critical" | "high" | "medium" | "low"> = [
        "critical",
        "high",
        "medium",
        "low",
    ];
    type TierKey = (typeof tierOrder)[number];
    const buckets = useMemo(() => {
        const raw = overview?.urgencyBuckets;
        if (!raw) return [];
        return tierOrder
            .map((tier) => {
                const b = raw[tier];
                if (!b) return null;
                return { tier, count: b.count, totalBalance: b.totalBalance };
            })
            .filter(
                (b): b is { tier: TierKey; count: number; totalBalance: number } =>
                    b !== null,
            );
    }, [overview]);
    const currency = "USD";

    const { series, options, totalAttention, totalDollars } = useMemo(() => {
        const labels = buckets.map((b) => TIER_LABELS[b.tier] || b.tier);
        const data = buckets.map((b) => b.count);
        const palette = buckets.map((b) => TIER_COLORS[b.tier] || "#9ca3af");
        const totals = buckets.map((b) => b.totalBalance);

        const opts: ApexOptions = {
            chart: { type: "donut", fontFamily: "'Montserrat', sans-serif" },
            labels,
            colors: palette,
            stroke: { width: 2, colors: ["#fff"] },
            legend: {
                position: "bottom",
                fontSize: "12px",
                markers: { width: 10, height: 10, radius: 5 },
            },
            dataLabels: {
                enabled: true,
                formatter: (val: number) => `${Math.round(val)}%`,
                style: { fontSize: "11px", fontWeight: 600 },
            },
            tooltip: {
                y: {
                    formatter: (val: number, opts) => {
                        const idx = opts.seriesIndex;
                        const amt = totals[idx] ?? 0;
                        return `${val} invoice${val === 1 ? "" : "s"} · ${fmtCurrency(amt, currency)}`;
                    },
                },
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: "65%",
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                label: "Open Invoices",
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#6b7280",
                                formatter: () =>
                                    String(data.reduce((s, v) => s + v, 0)),
                            },
                        },
                    },
                },
            },
        };

        const attention = buckets
            .filter((b) => b.tier === "critical" || b.tier === "high")
            .reduce(
                (acc, b) => ({
                    count: acc.count + b.count,
                    total: acc.total + b.totalBalance,
                }),
                { count: 0, total: 0 },
            );

        return {
            series: data,
            options: opts,
            totalAttention: attention.count,
            totalDollars: attention.total,
        };
    }, [buckets]);

    const hasData = series.some((s) => s > 0);

    return (
        <CollectSectionCard>
            <CollectSectionHeader>
                <Box>
                    <CollectSectionTitle>Urgency Breakdown</CollectSectionTitle>
                    <CollectSectionSubtitle>
                        How your open invoices stack up by how soon they need
                        attention.
                    </CollectSectionSubtitle>
                </Box>
            </CollectSectionHeader>

            {isLoading && !overview ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress sx={{ color: colors.primary }} />
                </Box>
            ) : !hasData ? (
                <Box py={4} textAlign="center">
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            color: "#6b7280",
                            fontSize: "0.9rem",
                        }}
                    >
                        No open invoices to score.
                    </Typography>
                </Box>
            ) : (
                <>
                    <Chart options={options} series={series} type="donut" height={260} />
                    <Box
                        sx={{
                            marginTop: "12px",
                            padding: "10px 14px",
                            borderRadius: "8px",
                            backgroundColor: totalAttention > 0 ? "#fef2f2" : "#f0fdf4",
                            border: `1px solid ${totalAttention > 0 ? "#fecaca" : "#bbf7d0"}`,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.85rem",
                                color: totalAttention > 0 ? "#b91c1c" : "#166534",
                                fontWeight: 500,
                            }}
                        >
                            {totalAttention > 0
                                ? `${totalAttention} invoice${totalAttention === 1 ? "" : "s"} (${fmtCurrency(totalDollars, currency)}) need your attention today.`
                                : "You're all caught up on critical and high-urgency items."}
                        </Typography>
                    </Box>
                </>
            )}
        </CollectSectionCard>
    );
};

export default UrgencyChart;
